import { useEffect, useMemo, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useOfflineQueue } from "@/hooks/useOfflineQueue";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle, Clock, RefreshCw, Trash2, ShieldOff, CloudOff } from "lucide-react";
import type { QueuedAction } from "@/lib/offlineQueue";

interface Props {
  /** Optional filter: only show queued attendance scans for this field group */
  fieldGroupId?: string;
}

function classifyError(msg: string | undefined): { kind: "rls" | "geofence" | "network" | "validation" | "other"; label: string } {
  const m = (msg ?? "").toLowerCase();
  if (m.includes("row-level security") || m.includes("rls") || m.includes("violates row-level"))
    return { kind: "rls", label: "Permission denied (RLS)" };
  if (m.includes("is_within_field_site") || m.includes("geofence") || m.includes("outside") || m.includes("within"))
    return { kind: "geofence", label: "Outside geofence" };
  if (m.includes("network") || m.includes("fetch") || m.includes("timeout"))
    return { kind: "network", label: "Network error" };
  if (m.includes("invalid") || m.includes("constraint") || m.includes("null value"))
    return { kind: "validation", label: "Validation error" };
  return { kind: "other", label: msg ? "Sync error" : "Unknown error" };
}

function isAttendanceForGroup(item: QueuedAction, groupId?: string): boolean {
  if (item.type !== "insert") return false;
  const p = item.payload as any;
  if (p?.table !== "attendance_records") return false;
  if (!groupId) return true;
  return p?.data?.field_group_id === groupId;
}

function describeScan(item: QueuedAction): string {
  const p = item.payload as any;
  const data = p?.data ?? {};
  const reg = data?.registration_number ?? data?.student_id?.slice?.(0, 8);
  const date = data?.attendance_date ?? "";
  return `${item.label}${reg ? ` · ${reg}` : ""}${date ? ` · ${date}` : ""}`;
}

export function QueuedScanStatus({ fieldGroupId }: Props) {
  const { queue, isOnline, isSyncing, runSync, retryItem, discardItem } = useOfflineQueue();
  const [now, setNow] = useState(Date.now());

  // Tick every 15s so backoff countdowns refresh
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(t);
  }, []);

  const items = useMemo(
    () => queue.filter((q) => isAttendanceForGroup(q, fieldGroupId)),
    [queue, fieldGroupId],
  );

  const failed = items.filter((i) => i.needsManualResolution);
  const retrying = items.filter((i) => !i.needsManualResolution && i.retries > 0);
  const pending = items.filter((i) => i.retries === 0 && !i.needsManualResolution);

  if (items.length === 0) return null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div>
          <CardTitle className="flex items-center gap-2 text-sm">
            <CloudOff className="w-4 h-4" /> Queued scans
            <Badge variant="outline" className="ml-1">{items.length}</Badge>
          </CardTitle>
          <CardDescription className="text-xs">
            {isOnline ? "Online — automatic sync runs in the background." : "Offline — scans will sync when the device reconnects."}
          </CardDescription>
        </div>
        <Button size="sm" variant="outline" onClick={runSync} disabled={isSyncing || !isOnline}>
          <RefreshCw className={`w-3 h-3 mr-1 ${isSyncing ? "animate-spin" : ""}`} /> Sync now
        </Button>
      </CardHeader>

      <CardContent className="space-y-3">
        {failed.length > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="w-4 h-4" />
            <AlertTitle>{failed.length} scan{failed.length > 1 ? "s" : ""} need{failed.length > 1 ? "" : "s"} manual resolution</AlertTitle>
            <AlertDescription>
              These scans were rejected by the server (likely RLS or geofence). Review and either retry or discard each one.
            </AlertDescription>
          </Alert>
        )}

        {failed.map((item) => {
          const { kind, label } = classifyError(item.lastError);
          const Icon = kind === "geofence" ? ShieldOff : kind === "rls" ? ShieldOff : AlertTriangle;
          return (
            <div key={item.id} className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="destructive" className="gap-1"><Icon className="w-3 h-3" /> {label}</Badge>
                    <span className="text-xs text-muted-foreground">
                      Failed {item.retries}× · queued {formatDistanceToNow(new Date(item.createdAt), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-sm font-medium mt-1 truncate">{describeScan(item)}</p>
                  {item.lastError && (
                    <p className="text-[11px] text-muted-foreground mt-1 break-words">{item.lastError}</p>
                  )}
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="outline" onClick={() => discardItem(item.id)}>
                  <Trash2 className="w-3 h-3 mr-1" /> Discard
                </Button>
                <Button size="sm" onClick={() => retryItem(item.id)} disabled={!isOnline || isSyncing}>
                  <RefreshCw className="w-3 h-3 mr-1" /> Retry
                </Button>
              </div>
            </div>
          );
        })}

        {(retrying.length > 0 || pending.length > 0) && (
          <div className="space-y-1">
            {retrying.map((item) => {
              const ms = item.nextRetryAt ? new Date(item.nextRetryAt).getTime() - now : 0;
              const inBackoff = ms > 0;
              return (
                <div key={item.id} className="flex items-center justify-between text-xs rounded border bg-muted/30 px-2 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <Clock className="w-3 h-3 text-muted-foreground shrink-0" />
                    <span className="truncate">{describeScan(item)}</span>
                  </div>
                  <span className="text-muted-foreground shrink-0 ml-2">
                    {inBackoff
                      ? `Retry in ${Math.max(1, Math.round(ms / 60000))} min · attempt ${item.retries}`
                      : `Retrying… attempt ${item.retries}`}
                  </span>
                </div>
              );
            })}
            {pending.map((item) => (
              <div key={item.id} className="flex items-center justify-between text-xs rounded border bg-muted/30 px-2 py-1.5">
                <div className="flex items-center gap-2 min-w-0">
                  <CloudOff className="w-3 h-3 text-muted-foreground shrink-0" />
                  <span className="truncate">{describeScan(item)}</span>
                </div>
                <span className="text-muted-foreground shrink-0 ml-2">Waiting to sync</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
